// Unified data source — delegates to the offline sim (MOCK) or the live Realtime
// REST+WS layer. Components/Terminal call these mode-agnostic functions; only this
// module knows which backend is in play.
import { MOCK } from '../lib/mode';
import { api } from '../lib/api';
import { ws } from '../lib/ws';
import { TF_TO_SERVER } from '../lib/constants';
import { mapSymbol, mapBars, mapLevels, mapTradeToPosition, quoteMid } from '../lib/mappers';
import { vwap } from '../lib/format';
import { type VisionLane, frameLane, getVisionLane, laneChannel, laneParam } from '../lib/vision-lane';
import * as mock from './mock';

type Unsub = () => void;

// Subscription receipts (window.__subCounts): live count per subscription
// family, counted at the CALL layer so MOCK counts identically to live — the
// same operator-checkable pattern as __rrPerf / __visionPerf. This is how the
// solo-tab fan-out claims are asserted headlessly (smoke-solo.mjs): a solo tab
// must show trades:1 / ladder:2 (its own chart + pinned alarm), never one
// subscription per watchlist row.
const subCounts: Record<string, number> = {};
if (typeof window !== 'undefined') (window as any).__subCounts = subCounts;
function counted(k: string, un: Unsub): Unsub {
  subCounts[k] = (subCounts[k] || 0) + 1;
  let done = false;
  return () => { if (!done) { done = true; subCounts[k]--; } un(); };
}

// ---- one-round-trip hydrate ----
// A symbol switch used to fan out ~10 REST calls; on a high-RTT link the
// request count (not payload size) dominated switch latency. /hydrate returns
// every per-symbol section in ONE response, and the getters below all read
// their slice from a short-lived shared hydrate promise. Any missing section —
// or an older server without the endpoint at all — falls back to the getter's
// individual endpoint, so a partial hydrate degrades to exactly the old
// behavior, never worse.
const HYDRATE_TTL_MS = 5000;
let hydCache: { key: string; sym: string; at: number; p: Promise<any | null> } | null = null;

// Getters that know the timeframe prime (or join) the hydrate for symbol+tf.
// The cache is set synchronously, so the burst of getters fired by one symbol
// switch (ChartPane + Terminal effects, same tick) shares a single request.
function hydrate(sym: string, serverTf: string, buyTf: string | null): Promise<any | null> {
  const key = `${sym}|${serverTf}|${buyTf || ''}`;
  if (hydCache && hydCache.key === key && Date.now() - hydCache.at < HYDRATE_TTL_MS) return hydCache.p;
  const p = api.getHydrate(sym, serverTf, buyTf).catch(() => null);
  hydCache = { key, sym, at: Date.now(), p };
  return p;
}

// Timeframe-less getters (ladder, supply, pbl, …) join whatever fresh hydrate
// exists for the symbol; they never trigger one (their tf context is unknown).
function hydrated(sym: string): Promise<any | null> {
  if (hydCache && hydCache.sym === sym && Date.now() - hydCache.at < HYDRATE_TTL_MS) return hydCache.p;
  return Promise.resolve(null);
}

// ---- historical candles (closed bars; every timeframe is a screener-stored bar) ----
export async function getCandles(symbol: string, tf: string) {
  if (MOCK) return mock.genCandles(symbol, tf, 130);
  const server = TF_TO_SERVER[tf] || '1Min';
  const h = await hydrate(symbol, server, TF_TO_BUYSCORE[tf] || null);
  const raw = h && h.bars ? (h.bars.bars ?? []) : await api.getBars(symbol, server);
  return mapBars(raw);
}

// ---- the screener's forming bar (mv5-prod/05 §3) ----
// { wm, forming, lastClosed } for the chart's timeframe, from the same hydrate as the candles (one round trip), or
// null (MOCK, an older server, Redis down) — the chart then starts from the closed bars and the live prints alone.
export async function getForming(symbol: string, tf: string): Promise<any | null> {
  if (MOCK) return null;
  const server = TF_TO_SERVER[tf] || '1Min';
  try {
    const h = await hydrate(symbol, server, TF_TO_BUYSCORE[tf] || null);
    if (h && h.forming) return h.forming;
    return await api.getForming(symbol, server);
  } catch {
    return null;
  }
}

// ---- indicator history from the server (real ema/sma/vwap snapshots) ----
// Returns [{ time(unix sec), values:{ema9,ema21,ema50,ema200,vwap,...} }] for every timeframe. Empty for MOCK,
// and (never throws) when the endpoint errors or a symbol has no indicators yet — the chart then computes the lines
// from the candles, so the bars still render instead of the whole chart failing on a missing indicator.
export async function getIndicators(symbol: string, tf: string) {
  if (MOCK) return [];
  const server = TF_TO_SERVER[tf] || '1Min';
  try {
    const h = await hydrate(symbol, server, TF_TO_BUYSCORE[tf] || null);
    const raw: any[] = h && h.indicators
      ? (h.indicators.indicators ?? h.indicators.history ?? [])
      : await api.getIndicators(symbol, server);
    return (raw || [])
      .map((h: any) => ({ time: Math.floor(new Date(h.timestamp || h.time || h.t).getTime() / 1000), values: h.values || h }))
      .filter((h: any) => !Number.isNaN(h.time));
  } catch {
    return [];
  }
}

// ---- buy score (chart sub-pane) ----
// Legend TF token → buy-score timeframe. Deliberately NOT TF_TO_SERVER: the scoring
// service DOES produce 3Min (it scores minute aggregates, it doesn't need 3Min bars),
// and it produces nothing above 1H — a single session makes 4H/D/W meaningless, so
// those TFs simply have no sub-pane.
const TF_TO_BUYSCORE: Record<string, string> = {
  '1': '1Min', '3': '3Min', '5': '5Min', '15': '15Min', '1H': '1H',
};

// Returns [{ time(unix sec), value(1-100), band }] ascending, or [] — never throws.
// Empty in MOCK, on an unsupported TF, and when the endpoint errors or the symbol has
// no witnessed tape yet; the chart reads [] as "don't create the pane" rather than
// failing the whole rebuild. Gaps are real (a minute nobody traded has no score) and
// are left as gaps — never zero-filled, which would read as heavy selling.
// `fresh` (the chart's 20 s refresh) reads the buy-score endpoint alone instead of re-pulling the whole hydrate.
export async function getBuyScores(symbol: string, tf: string, opts: { fresh?: boolean } = {}) {
  if (MOCK) return [];
  const server = TF_TO_BUYSCORE[tf];
  if (!server) return [];
  try {
    const barsTf = TF_TO_SERVER[tf] || '1Min';
    const h = opts.fresh ? null : await hydrate(symbol, barsTf, server);
    const raw: any[] = h && h.buyscore ? (h.buyscore.scores ?? []) : await api.getBuyScore(symbol, server);
    const pts = (raw || [])
      .map((p: any) => ({ time: Number(p.t), value: Number(p.v), band: String(p.b || 'balanced') }))
      .filter((p: any) => Number.isFinite(p.time) && Number.isFinite(p.value))
      .sort((a: any, b: any) => a.time - b.time);
    // lightweight-charts requires strictly ascending unique times; a duplicate bucket
    // would throw mid-rebuild and take the rest of the chart down with it.
    const out: any[] = [];
    for (const p of pts) {
      const last = out[out.length - 1];
      if (last && last.time === p.time) out[out.length - 1] = p;
      else out.push(p);
    }
    return out;
  } catch {
    return [];
  }
}

// ---- key levels (day H/L, PM high, VWAP, prev close) ----
export async function getKeyLevels(symbol: string, tf: string, meta?: any) {
  if (MOCK) return mock.keyLevels(mock.genCandles(symbol, tf, 130), symbol);
  const bars = await getCandles(symbol, tf);
  if (!bars.length) return { dayHigh: 0, dayLow: 0, pmHigh: 0, vwap: 0, prevClose: meta?.prevClose ?? 0 };
  let hi = -Infinity, lo = Infinity;
  for (const c of bars) { if (c.high > hi) hi = c.high; if (c.low < lo) lo = c.low; }
  const vw = vwap(bars as any);
  return {
    dayHigh: +hi.toFixed(2),
    dayLow: +lo.toFixed(2),
    pmHigh: +(hi * 0.985).toFixed(2),
    vwap: +vw[vw.length - 1].value.toFixed(2),
    prevClose: +(meta?.prevClose ?? bars[0].close).toFixed(2),
  };
}

// ---- support/resistance rail ----
export async function getSRLevels(symbol: string) {
  if (MOCK) return mock.computeLevels(symbol);
  const h = await hydrated(symbol);
  if (h && h.levels) return mapLevels(h.levels.levels ?? []);
  return mapLevels(await api.getLevels(symbol));
}

// ---- symbol universe (watchlist/movers seed) ----
export async function getSymbols() {
  if (MOCK) {
    return mock.SYMBOLS.map((s) => ({ ...s, last: s.base }));
  }
  return (await api.getActiveSymbols()).map(mapSymbol);
}

export async function searchSymbols(query: string) {
  if (MOCK) {
    const q = query.toLowerCase();
    return mock.SYMBOLS.filter((s) => s.ticker.toLowerCase().includes(q) || s.name.toLowerCase().includes(q)).map((s) => ({ ...s, last: s.base }));
  }
  return (await api.searchSymbols(query)).map(mapSymbol);
}

export async function setWatchlist(symbol: string, on: boolean, name?: string) {
  if (MOCK) return;
  await api.putSymbol(symbol, { isActivelyTrading: on, name });
}

// Active-symbol set changes (Mongo symbols change stream → `symbols` WS channel).
// Frames: { type:'symbol', data:{ symbol, isActivelyTrading } } — membership nudge
// only; the full meta (name/price/config) comes from re-fetching getSymbols().
export function subscribeSymbols(
  // tradingSide rides the same frame since the worker mirrors its own answer back
  // (a refused flip corrects the toggle live rather than only on reload).
  cb: (u: { symbol: string; isActivelyTrading: boolean; tradingSide?: 'long' | 'short' }) => void
): Unsub {
  if (MOCK) return counted('symbols', () => {});
  ws.subscribe('symbols');
  const off = ws.onMessage((m: any) => {
    if (m.type === 'symbol' && m.data && m.data.symbol) cb(m.data);
  }, { types: ['symbol'] });
  return counted('symbols', () => { ws.unsubscribe('symbols'); off(); });
}

// Per-symbol config (target preset, runner mode, trailing stop, automated signals).
// Trailing stop defaults OFF when the server has no explicit value (matches worker default).
export async function getSymbolConfig(symbol: string) {
  if (MOCK) return { targetPreset: 'NONE', runnerModeEnabled: true, trailingStopEnabled: false, trailingIndicator: 'levels', automatedSignalsEnabled: false, tradingSide: 'long' };
  try {
    const h = await hydrated(symbol);
    const s: any = (h && h.symbol_meta) || await api.getSymbol(symbol);
    return {
      targetPreset: s?.targetPreset || 'NONE',
      runnerModeEnabled: s?.runnerModeEnabled ?? true,
      trailingStopEnabled: s?.trailingStopEnabled ?? false,
      trailingIndicator: s?.trailingIndicator || 'levels',
      automatedSignalsEnabled: s?.automatedSignalsEnabled ?? false,
      accountSize: s?.accountSize ?? null,
      riskPerTradePercent: s?.riskPerTradePercent ?? null,
      maxPositionValuePercent: s?.maxPositionValuePercent ?? null,
      // Armed side. Anything other than the literal 'short' is long — the same
      // asymmetry the worker enforces (worker/trading-side.ts). A missing field,
      // a failed fetch or a typo must never arm a short.
      tradingSide: s?.tradingSide === 'short' ? 'short' : 'long',
    };
  } catch {
    return { targetPreset: 'NONE', runnerModeEnabled: true, trailingStopEnabled: false, trailingIndicator: 'levels', automatedSignalsEnabled: false, tradingSide: 'long' };
  }
}

// ---- positions (symbol → position model) ----
export async function getPositions() {
  if (MOCK) return mock.defaultPositions();
  const trades = await api.getTrades({ includeOrders: true });
  const out: Record<string, any> = {};
  for (const t of trades) {
    const pos = mapTradeToPosition(t);
    // Include open positions AND resting-only trades (qty 0 but a pending entry order).
    // /api/trades sorts createdAt DESC, so the FIRST hit per symbol is the engine's
    // newest doc — it must win. The old unconditional overwrite let the OLDEST open
    // doc win, so one stale orphan doc both resurrected its dead chip on every heal
    // and masked the newer trade's real position (FRTT 2026-08-11).
    if (pos && (pos.qty > 0 || (pos.entries && pos.entries.length))) {
      if (!out[t.symbol]) out[t.symbol] = pos;
    }
  }
  return out;
}

// ---- account summary inputs ----
export async function getAccount() {
  if (MOCK) return { cash: mock.MOCK_CASH };
  try {
    const a = await api.getAccount();
    // Alpaca returns these as strings — coerce to numbers (else `cash + mktVal`
    // string-concatenates into a garbled account value).
    const n = (v: any) => Number(v) || 0;
    return {
      cash: n(a.cash), equity: n(a.equity), buyingPower: n(a.buying_power),
      portfolioValue: n(a.portfolio_value), lastEquity: n(a.last_equity),
    };
  } catch {
    return { cash: 0, equity: 0, buyingPower: 0, portfolioValue: 0, lastEquity: 0 };
  }
}

// Live account pushes over the `account` channel: the backend syncs Alpaca → Mongo
// every second and broadcasts { type:'account', data:{ equity, cash, buyingPower,
// portfolioValue, lastEquity, ... } } — already camelCase numbers, unlike the REST
// doc. Without this the profile panel is a boot snapshot that never moves.
export function subscribeAccount(cb: (a: any) => void): Unsub {
  if (MOCK) return counted('account', () => {});
  ws.subscribe('account');
  const off = ws.onMessage((m: any) => {
    const d = m.data;
    if (m.type !== 'account' || !d) return;
    const n = (v: any) => Number(v) || 0;
    cb({
      cash: n(d.cash), equity: n(d.equity), buyingPower: n(d.buyingPower),
      portfolioValue: n(d.portfolioValue), lastEquity: n(d.lastEquity),
    });
  }, { types: ['account'] });
  return counted('account', () => { ws.unsubscribe('account'); off(); });
}

// ---- live subscriptions (live mode only; sim drives these in MOCK) ----
export function subscribeQuotes(symbol: string, cb: (q: any) => void): Unsub {
  if (MOCK) return mock.quoteTicker(symbol, (q: any) => cb({ ...q, mid: quoteMid(q) }));
  const ch = `quotes:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage(
    (m) => { if (m.type === 'quote' && m.symbol === symbol) cb({ ...m, mid: quoteMid(m) }); },
    { types: ['quote'], symbol },
  );
  return () => { ws.unsubscribe(ch); off(); };
}

// Live 1m indicator snapshot, published by the SCREENER on each completed bar
// (the same engine state that seeds worker bootstrap — what the trailing stop
// anchors to; NOT the chart's client-computed tails). The channel carries every
// timeframe the screener computes (1Min/5Min/15Min/1W), so the 1Min filter here
// is load-bearing: without it a 5m EMA9 overwrites the 1m EMA9 every 5 minutes.
// Frame: { type:'indicators', symbol, timeframe, ema9..ema200, vwap, atr, bars }.
export function subscribeIndicators(symbol: string, cb: (s: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `indicators:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage(
    (m) => { if (m.type === 'indicators' && m.symbol === symbol && m.timeframe === '1Min') cb(m); },
    { types: ['indicators'], symbol },
  );
  return () => { ws.unsubscribe(ch); off(); };
}

// The screener's forming snapshot for the symbol (mv5-prod/05 §3), every ≤ 500 ms while it trades and after each
// minute boundary: { type:'forming', symbol, asOf, wm, forming:{TF: bar|null}, lastClosed:{TF: bar|null} }.
export function subscribeForming(symbol: string, cb: (f: any) => void): Unsub {
  if (MOCK) return counted('forming', () => {});
  const ch = `forming:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage(
    (m) => { if (m.type === 'forming' && m.symbol === symbol && m.forming) cb(m); },
    { types: ['forming'], symbol },
  );
  return counted('forming', () => { ws.unsubscribe(ch); off(); });
}

// Real trade prints (the `market:<SYM>` channel = Redis trades:*). These carry the
// ACTUAL last-traded price ({ type:'trade', symbol, price, size, ... }, flat — no
// `.data`, which distinguishes them from the Mongo trade change stream). This is what
// Alpaca shows as "last", so the live price/candle should be driven by these.
export function subscribeTrades(symbol: string, cb: (t: any) => void): Unsub {
  if (MOCK) return counted('trades', () => {});
  const ch = `market:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage(
    (m) => { if (m.type === 'trade' && !m.data && m.symbol === symbol && m.price != null) cb(m); },
    { types: ['trade'], symbol },
  );
  return counted('trades', () => { ws.unsubscribe(ch); off(); });
}

// trade/order change streams for the active symbol → position updates
export function subscribeSymbolStreams(symbol: string, onTrade: (t: any) => void, onOrder: (o: any) => void): Unsub {
  if (MOCK) return () => {};
  const chs = [`trade:${symbol}`, `orders:${symbol}`];
  chs.forEach((c) => ws.subscribe(c));
  // No symbol in the filter: change-stream frames aren't guaranteed to carry a
  // top-level symbol (the handlers check m.data), so type-routing only.
  const off = ws.onMessage((m) => {
    if (m.type === 'trade' && m.data) onTrade(m.data);
    else if (m.type === 'order' && m.data && m.data.symbol === symbol) onOrder(m.data);
  }, { types: ['trade', 'order'] });
  return () => { chs.forEach((c) => ws.unsubscribe(c)); off(); };
}

// global trade/order streams (watchlist markers across all symbols)
export function subscribeGlobalTrades(onTrade: (t: any) => void): Unsub {
  if (MOCK) return () => {};
  ws.subscribe('trades');
  ws.subscribe('orders');
  const off = ws.onMessage((m) => { if (m.type === 'trade' && m.data) onTrade(m.data); }, { types: ['trade'] });
  return () => { ws.unsubscribe('trades'); ws.unsubscribe('orders'); off(); };
}

// global order change-stream (every symbol) — for fill/placement audio cues.
export function subscribeGlobalOrders(onOrder: (o: any) => void): Unsub {
  if (MOCK) return () => {};
  ws.subscribe('orders');
  const off = ws.onMessage((m: any) => { if (m.type === 'order' && m.data) onOrder(m.data); }, { types: ['order'] });
  return () => { ws.unsubscribe('orders'); off(); };
}

// ---- screener / automated-signals feed (realtime `watchlist` channel) ----
// Map a watchlist surge (REST result or WS push) → the signal-card shape.
function mapWatchlistSignal(m: any) {
  const buyVol = Number(m.buyVol ?? 0), sellVol = Number(m.sellVol ?? 0);
  const tot = buyVol + sellVol;
  const buyFrac = tot > 0 ? buyVol / tot : 0.5;
  const ts = typeof m.timestamp === 'number'
    ? m.timestamp
    : (m.timestamp ? new Date(m.timestamp).getTime() : Date.now());
  return {
    id: 'sig_' + m.symbol + '_' + ts,
    symbol: m.symbol,
    bucket: m.bucket || 'PRE_NEWS',
    score: Number(m.score ?? 0),
    price: Number(m.price ?? 0),
    priceMovePct: Number(m.priceMovePct ?? 0),
    rvol: Number(m.rvol ?? 0),
    surgeVol: Number(m.surgeVol ?? 0),
    buyVol, sellVol, buyFrac,
    sweep: Number(m.metrics?.sweep ?? m.sweep ?? 0),
    float: m.float != null ? Number(m.float) : null,
    // Prefer the realtime-computed flag; fall back to deriving it from float so
    // the badge still works against an older backend. Threshold matches the
    // server (src/apps/realtime watchlist.ts: <= 10M shares).
    lowFloat: m.lowFloat === true || (m.float != null && Number(m.float) <= 10_000_000),
    country: m.country ?? null,
    flag: m.countryFlag ?? null,
    ts,
  };
}

// Seed the feed from the REST snapshot (strongest first).
export async function getScreenerSeed() {
  if (MOCK) return mock.seedSignals(8);
  try {
    const rows: any[] = await api.getWatchlist({ limit: 100 });
    return (rows || []).map(mapWatchlistSignal);
  } catch { return []; }
}

// Live surges from the `watchlist` WS channel (no auth needed).
export function subscribeScreener(cb: (s: any) => void): Unsub {
  if (MOCK) return counted('screener', () => {});
  ws.subscribe('watchlist');
  const off = ws.onMessage((m: any) => { if (m.type === 'watchlist' && m.symbol) cb(mapWatchlistSignal(m)); }, { types: ['watchlist'] });
  return counted('screener', () => { ws.unsubscribe('watchlist'); off(); });
}

// ---- screener SHORT / flush feed (realtime `watchlist-short` channel) ----
// The sell-led mirror of the two above. Same alert shape and therefore the same
// mapper — the screener's DOWN alerts carry direction-relative metrics (sell
// share, bid levels broken), which the card renders via its `short` flag.
// Separate REST lane (`direction=DOWN`) and separate WS channel, so nothing a
// flush publishes can reach the long feed.
export async function getScreenerShortSeed() {
  if (MOCK) return mock.seedShortSignals(6);
  try {
    const rows: any[] = await api.getWatchlist({ limit: 100, direction: 'DOWN' });
    return (rows || []).map(mapWatchlistSignal);
  } catch { return []; }
}

export function subscribeScreenerShort(cb: (s: any) => void): Unsub {
  if (MOCK) return counted('screener-short', () => {});
  ws.subscribe('watchlist-short');
  const off = ws.onMessage((m: any) => { if (m.type === 'watchlist-short' && m.symbol) cb(mapWatchlistSignal(m)); }, { types: ['watchlist-short'] });
  return counted('screener-short', () => { ws.unsubscribe('watchlist-short'); off(); });
}

// ---- heat leaderboard (rotation radar — 1 Hz cross-sectional tape heat) ----
// The ladder-service ranks watchlist tape heat once a second and publishes the
// board on the single GLOBAL `heat:leaderboard` channel, plus a `heat:rotation`
// event when a symbol rotates INTO the hot set (hysteresis satisfied). One
// subscription each — NOT per-symbol. Frames pass through
// raw; the HeatStrip owns the reduce.
export function subscribeHeatLeaderboard(cb: (board: any) => void): Unsub {
  if (MOCK) return () => {};
  ws.subscribe('heat:leaderboard');
  const off = ws.onMessage((m: any) => {
    if (m.type === 'heat-leaderboard' && Array.isArray(m.symbols)) cb(m);
  }, { types: ['heat-leaderboard'] });
  return () => { ws.unsubscribe('heat:leaderboard'); off(); };
}

export function subscribeHeatRotation(cb: (ev: any) => void): Unsub {
  if (MOCK) return () => {};
  ws.subscribe('heat:rotation');
  const off = ws.onMessage((m: any) => {
    if (m.type === 'heat-rotation' && m.symbol) cb(m);
  }, { types: ['heat-rotation'] });
  return () => { ws.unsubscribe('heat:rotation'); off(); };
}

// ---- Momentum Vision detection frames (vision-service) ----
// LANE (lib/vision-lane.ts): 'mv5' = `vision-mv5:<SYM>` + REST `?lane=mv5`, the
// only lane since MV-4 (the unsuffixed `vision:<SYM>`) was retired on 2026-10-25.
// Each consumer passes the lane it renders (useVisionLane) and re-keys its
// hydrate+subscribe on it; omitted = the page's current lane.
//
// REST hydrate of the service-maintained `worker:vision-<lane>:{SYM}`
// snapshot: { symbol, lane, active, frame }. Null in MOCK (no vision sim —
// the layer shows "no feed", same precedent as supply/pbl/rr).
export async function getVision(symbol: string, lane: VisionLane = getVisionLane()) {
  if (MOCK) return null;
  try {
    const r = await api.getVision(symbol, laneParam(lane));
    // An answer that does not name the lane asked for is no feed — never show
    // another publisher's frame under this lane's name.
    if (r && r.lane !== lane) return { symbol, lane, active: false, frame: null };
    return r;
  } catch { return null; }
}

// Live vision feed: full-snapshot { type:'vision', symbol, seq, ts, objects,
// levels, ... } frames. Each frame REPLACES the previous one wholesale (never
// an append log) — the VisionLayer redraws its canvas from the latest frame only.
// Every lane arrives as type 'vision' for the same symbol on one socket; the
// relay's `lane` stamp keeps them apart, so a frame from another publisher
// never reaches this one.
export function subscribeVision(symbol: string, cb: (frame: any) => void, lane: VisionLane = getVisionLane()): Unsub {
  if (MOCK) return () => {};
  const ch = laneChannel(symbol, lane);
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'vision' && m.symbol === symbol && frameLane(m) === lane) cb(m);
  }, { types: ['vision'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// ---- supply card + timeline model (supply_timeline collections) ----
// REST hydrate only: the model is a daily history, so nothing pushes it intraday.
// A hydrate whose supply section is null means "no timeline" — no second request.
// MOCK has no supply sim → null (card hides).
export async function getSupply(symbol: string) {
  if (MOCK) return null;
  try {
    const h = await hydrated(symbol);
    if (h && 'supply' in h) return h.supply ? (h.supply.model ?? null) : null;
    return await api.getSupply(symbol);
  } catch { return null; }
}

// ---- news headlines (news-service → symbols.news, hydrate + news:<SYM> WS) ----
// Returns { items: [{ id, headline, source, url, publishedAt, updatedAt }, …] }
// newest-first, or null when no headline has ever been stored (card section hides).
export async function getNews(symbol: string) {
  if (MOCK) return null;
  try {
    const h = await hydrated(symbol);
    if (h && h.news) return h.news.news ?? null;
    return await api.getNews(symbol);
  } catch { return null; }
}

// Live pushes over `news:<SYM>`: { type:'news', symbol, data: { items } }.
// Fired by the Mongo change-stream bridge when the news-service writes a
// headline. `data` is the WHOLE news object — replace, don't merge.
export function subscribeNews(symbol: string, cb: (news: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `news:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'news' && m.symbol === symbol && m.data) cb(m.data);
  }, { types: ['news'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// ---- global news wire (News rail widget) ----
// Seed: one row per symbol (its latest stored headline), newest first.
export async function getNewsFeedSeed(limit = 50) {
  if (MOCK) return [];
  try { return await api.getNewsFeed({ limit }); } catch { return []; }
}

// Live pushes over the flat `news:feed` channel — one frame per news-service
// write: { type:'news-feed', symbol, data:{ id, headline, source, url,
// publishedAt, updatedAt, floatShares, marketCap } }. The rail replaces its
// row for that symbol. Subscribed ONLY while a News rail widget is mounted,
// so a terminal without the widget pays nothing for the wire.
export function subscribeNewsFeed(cb: (row: any) => void): Unsub {
  if (MOCK) return () => {};
  ws.subscribe('news:feed');
  const off = ws.onMessage((m: any) => {
    if (m.type === 'news-feed' && m.symbol && m.data) cb({ symbol: m.symbol, ...m.data });
  }, { types: ['news-feed'] });
  return () => { ws.unsubscribe('news:feed'); off(); };
}

// ---- company "about" snapshot (StockInfo card) ----
// The FMP profile fields the single-symbol route projects (description, sector,
// market cap…) — hydrate's symbol_meta section when fresh, else one REST call.
export async function getAbout(symbol: string) {
  if (MOCK) return null;
  try {
    const h = await hydrated(symbol);
    const s: any = (h && h.symbol_meta) || await api.getSymbol(symbol);
    if (!s || !s.symbol) return null;
    return {
      name: s.name || s.companyName || symbol,
      description: s.description ?? null,
      website: s.website ?? null,
      sector: s.sector ?? null,
      industry: s.industry ?? null,
      exchange: s.exchange ?? null,
      marketCap: s.marketCap ?? null,
      avgVolume: s.avgVolume ?? null,
    };
  } catch { return null; }
}

// ---- borrow / short availability (pre-flight before a short) ----
// REST hydrate only: the broker facts are swept nightly, so there is nothing to
// push. Every number the card shows is derived client-side from these facts
// plus the LIVE quote (lib/borrow.ts) — the facts are daily, the maths is live.
// Null in MOCK / when the symbol has never been swept (the card hides).
export async function getBorrow(symbol: string) {
  if (MOCK) return null;
  try {
    const r = await api.getBorrow(symbol);
    return r && r.active ? r : null;
  } catch { return null; }
}

/**
 * The locates the fleet holds, account-wide — the answer to "what borrow do we
 * own?", which is not a property of whatever symbol is on screen.
 *
 * POLLED, not pushed: locates are written to Redis by the workers, and nothing
 * bridges that to the WS the way the Mongo change stream bridges the sweep. A
 * purchase lands within one poll, which is the same latency the operator sees
 * in the worker log.
 *
 * Returns a map keyed by symbol so callers can ask about one without a scan.
 * An empty map on failure would read as "we hold nothing"; null says "we do not
 * know", and the callers keep the last good answer.
 */
export async function getLocates(): Promise<Record<string, any> | null> {
  if (MOCK) return null;
  try {
    const r = await api.getLocates();
    const rows = Array.isArray(r?.locates) ? r.locates : null;
    if (!rows) return null;
    const by: Record<string, any> = {};
    for (const row of rows) if (row?.symbol) by[String(row.symbol).toUpperCase()] = row;
    return by;
  } catch { return null; }
}

// Live pushes over `borrow:<SYM>`: { type:'borrow', symbol, data: BorrowFacts }.
// Fired by the Mongo change-stream bridge whenever a sweep writes — the nightly
// 04:00 ET run (which carries the between-session repricing) and the intraday
// watchlist top-up. Only the FACTS arrive; the card re-derives every displayed
// number from them and its own live quote.
export function subscribeBorrow(symbol: string, cb: (facts: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `borrow:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'borrow' && m.symbol === symbol && m.data) cb(m.data);
  }, { types: ['borrow'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// ---- ladder order-flow (per-price volume profile + verdict states) ----
// REST hydrate of the screener LadderEngine snapshot: { active, snapshot, signals }.
// Null in MOCK / when the symbol isn't currently laddered (no live tape).
export async function getLadder(symbol: string) {
  if (MOCK) return null;
  try {
    const h = await hydrated(symbol);
    return (h && h.ladder) || await api.getLadder(symbol);
  } catch { return null; }
}

// Live ladder feed over the `ladder:<SYM>` channel. Three frame shapes on one
// channel — full snapshot (type:'ladder'), row delta (type:'ladder-update'), and
// a state emission with metrics+evidence (type:'ladder-signal'). Passes each
// frame through raw; the LadderCard hook reduces them into its display snapshot.
export function subscribeLadder(symbol: string, cb: (frame: any) => void): Unsub {
  if (MOCK) return counted('ladder', () => {});
  const ch = `ladder:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if ((m.type === 'ladder' || m.type === 'ladder-update' || m.type === 'ladder-signal') && m.symbol === symbol) cb(m);
  }, { types: ['ladder', 'ladder-update', 'ladder-signal'], symbol });
  return counted('ladder', () => { ws.unsubscribe(ch); off(); });
}

// REST hydrate of the worker's PBL-v12 pullback-signal receipts (SIGNAL-ONLY):
// { symbol, active, signals }. Null in MOCK / when the symbol has no signals today.
export async function getPbl(symbol: string) {
  if (MOCK) return null;
  try {
    const h = await hydrated(symbol);
    return (h && h.pbl) || await api.getPbl(symbol);
  } catch { return null; }
}

// Live PBL signals over the `pbl:<SYM>` channel: one { type:'pbl-signal', symbol,
// action, px, stopRef, grade, entryType, ... } frame per would-be command. Passes
// each frame through raw; the PblCard prepends it to its signal list.
export function subscribePbl(symbol: string, cb: (frame: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `pbl:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'pbl-signal' && m.symbol === symbol) cb(m);
  }, { types: ['pbl-signal'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// REST hydrate of the worker's live risk/reward preview (READ-ONLY): envelope
// { symbol, active, frame } where `frame` is the last published rr-update
// snapshot. Null in MOCK / when the worker never published (card hides — same
// precedent as supply/pbl/journal). Spec: architecture/rr-preview-feed.md
export async function getRr(symbol: string) {
  if (MOCK) return null;
  try { return await api.getRr(symbol); } catch { return null; }
}

// Live RR preview over the `rr:<SYM>` channel: full-snapshot { type:'rr-update',
// symbol, mode, entryPreview, position?, scaleInPreview?, ... } frames. Each
// frame REPLACES the previous one wholesale (never an append log) — the RrCard
// computes the live ratios itself from its own ticking last price.
export function subscribeRr(symbol: string, cb: (frame: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `rr:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'rr-update' && m.symbol === symbol) cb(m);
  }, { types: ['rr-update'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// The worker's refusal of a command, on the same `rr:<SYM>` channel: { type:'command-rejected', symbol, ts,
// commandType, stage, reason, text } — `text` is one plain line for the toast. The realtime ack only means the
// command reached the worker's stream; this is the worker saying it did NOT act (worker command-feedback.ts).
export function subscribeCommandRejections(symbol: string, cb: (frame: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `rr:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (m.type === 'command-rejected' && m.symbol === symbol) cb(m);
  }, { types: ['command-rejected'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

// ---- trade journal (per-symbol round-trip history) ----
// REST hydrate of the server-shaped book: { symbol, open, closed }. Null in MOCK
// (no journal sim — the card hides, same precedent as supply/pbl).
export async function getJournal(symbol: string) {
  if (MOCK) return null;
  try {
    const r: any = await api.getJournal(symbol);
    return { symbol: r.symbol || symbol, open: r.open || null, closed: r.closed || [] };
  } catch { return null; }
}

// ---- his entry record (the journal: trades by third of the 5-minute range, chasing) ----
// The entry marks' colours (lib/entry-marks.ts). Null in MOCK or on any failure: the marks then use the record they
// shipped with (ENTRY_RECORD).
export async function getEntryRecord() {
  if (MOCK) return null;
  try { return await api.getEntryRecord(); } catch { return null; }
}

// Live journal invalidation. The Mongo change-stream bridge already broadcasts
// every trade-doc write on `trade:{SYM}` and every order write on `orders:{SYM}`;
// this surfaces a "something changed — re-hydrate" tick rather than reducing
// frames client-side (the book needs the orders join only the server can do
// cheaply). The caller debounces the refetch. Only frames that can move a
// journal number fire: order frames with executed shares (fills land here) and
// trade-close frames (exit block/realizedPnL) — NOT the 1 Hz position-price
// heartbeat the trade stream carries, which would otherwise poll the route all
// session. ws.subscribe is ref-counted, so sharing these channels with
// subscribeSymbolStreams is fine.
export function subscribeJournal(symbol: string, cb: () => void): Unsub {
  if (MOCK) return () => {};
  const chs = [`trade:${symbol}`, `orders:${symbol}`];
  chs.forEach((c) => ws.subscribe(c));
  // Change-stream frames aren't guaranteed to carry a top-level symbol — check
  // m.data, and fire on symbol-less frames rather than miss a close.
  const off = ws.onMessage((m: any) => {
    const d = m.data;
    if (!d || (d.symbol && d.symbol !== symbol)) return;
    if (m.type === 'order' && Number(d.filledQty) > 0) cb();
    else if (m.type === 'trade' && d.status === 'closed') cb();
  }, { types: ['trade', 'order'] });
  return () => { chs.forEach((c) => ws.unsubscribe(c)); off(); };
}

// ---- structure / level map (StructureRail) ----
// The realtime feed delivers the PROJECTED level list ({id,type,side,px,tier,weight,
// strength,touches,trades,timeframe,status,lastTestedAt} + metadata). Group it by side
// into the { supports, resistances, flipped, sessionATR } shape the rail clusters.
function projectedToStructure(resp: any) {
  const md = resp?.metadata || {};
  const supports: any[] = [], resistances: any[] = [], flipped: any[] = [];
  for (const l of resp?.levels || []) {
    const lvl = { ...l, price: l.px }; // alias px → price for clusterStructure
    if (l.side === 'resistance') resistances.push(lvl);
    else if (l.side === 'flipped') flipped.push(lvl);
    else supports.push(lvl);
  }
  return { symbol: resp?.symbol, sessionATR: md.sessionATR || 0.01, lastPrice: md.lastPrice, updatedAt: md.updatedAt, source: md.source,
    // session extremes feed the Main mode's structure guard + extension tag (lib/structure.ts mainView)
    sessionRunHigh: md.sessionRunHigh ?? null, sessionBaseLow: md.sessionBaseLow ?? null,
    supports, resistances, flipped };
}

// Hydrate the level map (REST). MOCK uses the sim; live reads /api/symbols/:sym/levels.
export async function getStructure(symbol: string, lastPrice: number) {
  if (MOCK) return mock.computeStructure(symbol, lastPrice);
  try {
    const h = await hydrated(symbol);
    if (h && h.levels) return projectedToStructure({ levels: h.levels.levels ?? [], metadata: h.levels.metadata ?? {}, symbol: h.levels.symbol ?? symbol });
    return projectedToStructure(await api.getLevelMap(symbol));
  } catch { return null; }
}

// Live levels over WS (`levels:<SYM>` channel): full `levels` snapshots + `level-update`
// deltas. Calls back with { kind:'snapshot', structure } or { kind:'delta', delta }.
export function subscribeStructure(symbol: string, cb: (evt: any) => void): Unsub {
  if (MOCK) return () => {};
  const ch = `levels:${symbol}`;
  ws.subscribe(ch);
  const off = ws.onMessage((m: any) => {
    if (!m || m.symbol !== symbol) return;
    if (m.type === 'levels') cb({ kind: 'snapshot', structure: projectedToStructure(m) });
    else if (m.type === 'level-update') cb({ kind: 'delta', delta: m });
  }, { types: ['levels', 'level-update'], symbol });
  return () => { ws.unsubscribe(ch); off(); };
}

export { MOCK };
